import { Button, Grid, Rating, Typography } from '@mui/material';
import { Formik } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { AccountData, Language, StorefrontReviewData } from '../../../api';
import TitledDrawer from '../../../common/ui/TitledDrawer';
import { FormTextField } from '../../../experienceSettings/formComponents/FormTextField';
import { useAuth } from '../../../session/InternalAuthProvider';

type ReviewEditDialogProps = {
  open: boolean;
  onClose: () => void;
  onSave: (review: StorefrontReviewData) => void;
  review: StorefrontReviewData | null;
};

type ReviewFormValues = {
  reviewer_name: Record<string, string>;
  score: number;
  text: Record<string, string>;
};

export const ReviewEditDialog = ({ open, onClose, onSave, review }: ReviewEditDialogProps) => {
  const { t } = useTranslation();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const languages = account.customer_facing_widgets_languages;

  const initialValues: ReviewFormValues = {
    reviewer_name: review?.reviewer_name || {},
    score: review?.score || 5,
    text: review?.text || {},
  };

  const handleSubmit = (values: ReviewFormValues) => {
    onSave(values);
    onClose();
  };

  const isEdit = review !== null;

  return (
    <TitledDrawer
      title={isEdit ? t('storefronts.edit_review') : t('storefronts.add_review')}
      open={open}
      onClose={onClose}>
      <Formik initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize>
        {({ values, setFieldValue, submitForm }) => (
          <>
            <Grid
              container
              flexDirection="column"
              flexWrap="nowrap"
              gap={1.5}
              flex={1}
              minHeight={0}
              sx={{
                overflowY: 'auto',
                pt: { xs: '12px', md: '18px' },
                pb: 2,
                paddingInline: { xs: '12px', md: '24px' },
              }}>
              {languages.map((lang) => (
                <FormTextField
                  key={`name_${lang}`}
                  fieldName={`reviewer_name.${lang}`}
                  label={t('storefronts.reviewer_name_lang', { lang: t(`emailTemplates.${lang}`) })}
                  dir={lang === Language.HE ? 'rtl' : 'ltr'}
                  width="100%"
                  isFieldUpdate
                />
              ))}
              <Grid container alignItems="center" gap={1}>
                <Typography>{t('storefronts.rating')}:</Typography>
                <Rating
                  value={values.score}
                  precision={0.5}
                  onChange={(_, value) => setFieldValue('score', value || 5)}
                />
              </Grid>
              {languages.map((lang) => (
                <FormTextField
                  key={`text_${lang}`}
                  fieldName={`text.${lang}`}
                  label={t('storefronts.review_text_lang', { lang: t(`emailTemplates.${lang}`) })}
                  dir={lang === Language.HE ? 'rtl' : 'ltr'}
                  minRows={2}
                  width="100%"
                  isFieldUpdate
                />
              ))}
            </Grid>
            <Grid
              container
              gap={1}
              justifyContent="flex-end"
              sx={{
                flexShrink: 0,
                py: 1.5,
                paddingInline: { xs: '12px', md: '24px' },
                borderTop: '1px solid',
                borderColor: 'mb.lineSoft',
              }}>
              <Button variant="outlined" color="inherit" onClick={onClose}>
                {t('cancel')}
              </Button>
              <Button variant="contained" onClick={submitForm}>
                {t('save')}
              </Button>
            </Grid>
          </>
        )}
      </Formik>
    </TitledDrawer>
  );
};
